import { addDoc, collection, doc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { deviceFields, getClientDevice } from './deviceInfo';
import { geoSnapshot } from './activityLog';

const PENDING_INTENT_KEY = 'cfa_pending_account_intent_v1';
const PURCHASE_EVENTS = new Set([
  'pricing_view',
  'paywall_view',
  'purchase_intent',
  'purchase_click',
  'checkout_started',
  'checkout_paid',
]);

function cleanProps(props = {}) {
  return Object.fromEntries(
    Object.entries(props)
      .filter(([, value]) =>
        value === null || ['string', 'number', 'boolean'].includes(typeof value)
      )
      .map(([key, value]) => [String(key).slice(0, 60), typeof value === 'string' ? value.slice(0, 300) : value])
  );
}

export function rememberAccountIntent(eventName, props = {}) {
  if (typeof window === 'undefined' || !eventName) return;
  try {
    const existing = readPendingAccountIntent();
    const sameIntent = existing?.eventName === eventName;
    sessionStorage.setItem(
      PENDING_INTENT_KEY,
      JSON.stringify({
        eventName,
        props: cleanProps({ ...(sameIntent ? existing.props : {}), ...props }),
        createdAt: sameIntent && existing.createdAt ? existing.createdAt : new Date().toISOString(),
      })
    );
  } catch {
    /* private mode */
  }
}

export function readPendingAccountIntent() {
  try {
    const raw = sessionStorage.getItem(PENDING_INTENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearPendingAccountIntent() {
  try {
    sessionStorage.removeItem(PENDING_INTENT_KEY);
  } catch {
    /* ignore */
  }
}

/** Store behavioral and commercial intent against the authenticated profile. */
export async function recordAccountIntent(eventName, props = {}, explicitUser = null) {
  const user = explicitUser || auth.currentUser;
  if (!user?.uid || !eventName) return false;

  const at = Timestamp.now();
  const event = String(eventName).slice(0, 80);
  const details = cleanProps(props);
  const summary = {
    lastIntentEvent: event,
    lastIntentAt: at,
  };
  if (PURCHASE_EVENTS.has(event)) {
    summary.purchaseIntentAt = at;
    summary.purchaseIntentStage = event;
  }

  await Promise.all([
    addDoc(collection(db, 'userProfiles', user.uid, 'intentEvents'), {
      event,
      ...details,
      ...deviceFields(getClientDevice()),
      ...geoSnapshot(),
      at,
    }),
    setDoc(doc(db, 'userProfiles', user.uid), summary, { merge: true }),
  ]);
  return true;
}

export async function consumePendingAccountIntent(user) {
  const pending = readPendingAccountIntent();
  if (!pending?.eventName || !user?.uid) return false;
  try {
    await recordAccountIntent(
      pending.eventName,
      { ...pending.props, capturedAfterAuth: true, originallyAt: pending.createdAt || '' },
      user
    );
    clearPendingAccountIntent();
    return true;
  } catch {
    return false;
  }
}
